"use client";

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Card, CardContent } from '@vaiu/ui/card';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { useAuth } from '@/context/AuthContext';
import { platformApi } from '@/services/platformApi';
import { i18n } from '@/utils/i18n';
import OTPLogin from '@/components/OTPLogin';
import GoogleSignIn from '@/components/GoogleSignIn';
import { usePostAuthRouting } from '@/hooks/usePostAuthRouting';
import BiometricLogin from '@/components/BiometricLogin';
import { Eye, EyeOff, UserX, LogIn } from 'lucide-react';
import { analyticsService } from '@/services/analyticsService';
import { getDefaultAuthenticatedDestination } from '@/utils/domainDetection';

interface LoginProps {
  onSuccess?: () => void;
  redirectTo?: string;
  showHeader?: boolean;
  showGuestOption?: boolean;
  className?: string;
}

const Login: React.FC<LoginProps> = ({ 
  onSuccess, 
  redirectTo,
  showHeader = true,
  showGuestOption = true,
  className = ""
}) => {
  const [loginInput, setLoginInput] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOTPLogin, setShowOTPLogin] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, user, loading: authLoading, isGuest } = useAuth();
  const { handlePostLoginRouting } = usePostAuthRouting();
  
  // Check if this login is for redeem purposes (hide guest option)
  const isForRedeem = searchParams.get('forRedeem') === 'true';
  
  // Get redirect URL from props, location state, or query parameters
  const from = redirectTo || location.state?.from?.pathname || searchParams.get('redirect') || getDefaultAuthenticatedDestination();
  
  // Override showGuestOption if this is for redeem
  const shouldShowGuestOption = showGuestOption && !isForRedeem;

  // If user is already logged in (non-guest), redirect
  useEffect(() => {
    if (!authLoading && user && !isGuest) {
      if (onSuccess) {
        onSuccess();
      } else {
        // Use routing service to determine destination
        handlePostLoginRouting();
      }
    }
  }, [user, authLoading, isGuest, onSuccess, handlePostLoginRouting]);

  // Clear any previous errors when user starts typing
  useEffect(() => {
    if (error) {
      setError('');
    }
  }, [loginInput, password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate form inputs
    if (!loginInput.trim()) {
      setError(i18n.__('login.usernameOrEmailRequired'));
      return;
    }
    
    if (!password.trim()) {
      setError(i18n.__('login.passwordRequired'));
      return;
    }

    setLoading(true);

    try {
      console.log('🔐 Starting login process with input:', loginInput);
      
      // Use platformApi login method which handles email vs username detection
      const result = await platformApi.login(loginInput.trim(), password);

      console.log('✅ Login API successful, result:', result);

      if (result.token) {
        console.log('🔑 Token received, calling AuthContext login...');
        await login(result.token);
        
        // Call success callback or use routing service
        if (onSuccess) {
          onSuccess();
        } else {
          console.log('🏠 Using routing service for post-login navigation');
          await handlePostLoginRouting();
        }
      } else {
        throw new Error('No authentication token received from server');
      }

    } catch (err: any) {
      console.error('❌ Login error:', err);
      
      // Provide user-friendly error messages
      let errorMessage = err.message || 'Login failed';
      
      // Map common errors to user-friendly messages
      if (errorMessage.includes('Authentication required') || errorMessage.includes('401')) {
        errorMessage = i18n.__('login.invalidCredentials');
      } else if (errorMessage.includes('Network error')) {
        errorMessage = i18n.__('login.networkError');
      } else if (errorMessage.includes('timeout')) {
        errorMessage = i18n.__('login.timeout');
      } else if (errorMessage.includes('Unexpected token')) {
        errorMessage = i18n.__('login.serverError');
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSuccess = async (token: string) => {
    try {
      await login(token);
      setShowOTPLogin(false);
      
      // Call success callback or navigate
      if (onSuccess) {
        onSuccess();
      } else {
        console.log('🏠 Navigating to:', from, 'after OTP/biometric login');
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      console.error('OTP login error:', err);
      setError(i18n.__('login.otpLoginFailed'));
    }
  };

  const handleGuestLogin = async () => {
    setError('');
    setLoading(true);

    try {
      console.log('👤 Starting guest login process...');
      
      // Add timeout to prevent infinite loading (15 seconds)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error(i18n.__('login.guestLoginTimeout'))), 15000)
      );
      
      // Race between API call and timeout
      const result = await Promise.race([
        platformApi.guestLogin(),
        timeoutPromise
      ]) as any;
      
      console.log('✅ Guest login successful, result:', result);

      if (result.token) {
        console.log('🔑 Guest token received, logging in...');
        
        // Track guest login in analytics (if available)
        if (typeof analyticsService !== 'undefined' && analyticsService.trackGuestLogin) {
          try {
            analyticsService.trackGuestLogin(result.username, result.sessionExpiry);
          } catch (analyticsError) {
            console.warn('Analytics tracking failed:', analyticsError);
            // Don't fail the login if analytics fails
          }
        }
        
        // Pass isGuestLogin flag and guest data to handle guest login differently
        await login(result.token, true, {
          username: result.username,
          userId: result.userId,
          sessionExpiry: result.sessionExpiry
        });
        
        // Call success callback or navigate
        if (onSuccess) {
          onSuccess();
        } else {
          console.log('🏠 Navigating guest to:', from);
          navigate(from, { replace: true, state: { isGuest: true } });
        }
      } else {
        throw new Error(i18n.__('login.noToken'));
      }
    } catch (err: any) {
      console.error('❌ Guest login error:', err);
      
      let errorMessage = err.message || i18n.__('login.guestLoginFailed');
      
      // More specific error messages
      if (errorMessage.includes('timeout')) {
        errorMessage = i18n.__('login.guestLoginTimeout');
      } else if (errorMessage.includes('Network error') || errorMessage.includes('fetch')) {
        errorMessage = i18n.__('login.guestLoginNetworkError');
      } else if (errorMessage.includes('401') || errorMessage.includes('403')) {
        errorMessage = i18n.__('login.guestLoginUnauthorized');
      } else if (errorMessage.includes('404')) {
        errorMessage = i18n.__('login.guestLoginNotFound');
      } else if (errorMessage.includes('500') || errorMessage.includes('Internal')) {
        errorMessage = i18n.__('login.serverError');
      }
      
      setError(errorMessage);
      
      // Show the error in the UI
      alert(`Guest Login Error: ${errorMessage}\n\nCheck browser console for details.`);
    } finally {
      setLoading(false);
    }
  };

  if (showOTPLogin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <OTPLogin 
          onSuccess={handleOTPSuccess}
          onBack={() => setShowOTPLogin(false)}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <Card>
        <CardContent className="pt-6">
          {showHeader && (
            <div className="text-center mb-4">
              <h2 className="text-2xl font-bold">{i18n.__('vaiu.sign_in') || 'Sign In'}</h2>
              <div className="text-sm text-gray-600 mt-2">
                {i18n.__('login.no_account_question')}{' '}
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto text-blue-600 hover:underline"
                  onClick={() => navigate('/signup')}
                >
                  {i18n.__('vaiu.sign_up')}
                </Button>
              </div>
            </div>
          )}
          
          <div className="space-y-3 mb-4">
            {shouldShowGuestOption && (
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50/50">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full flex items-center justify-center space-x-2 bg-white hover:bg-gray-50"
                  onClick={handleGuestLogin}
                  disabled={loading}
                  data-guest-login-button
                >
                  <UserX className="w-4 h-4" />
                  <span>{i18n.__('guest.continue_as_guest') || 'Continue as Guest'}</span>
                </Button>
                <p className="text-xs text-gray-600 text-center mt-2 px-2">
                  {i18n.__('guest.login_description') || 'Full access for 48 hours. Sign-up to be able to login'}
                </p>
              </div>
            )}

            <Button 
              type="button" 
              variant="outline" 
              className="w-full h-10" 
              onClick={() => setShowOTPLogin(true)}
            >
              {i18n.__('login.with_phone')}
            </Button>

            <GoogleSignIn
              onSuccess={handleOTPSuccess}
              onError={(error) => setError(error)}
              disabled={loading}
            />

            <BiometricLogin
              onSuccess={handleOTPSuccess}
              onError={(error) => setError(error)}
              disabled={loading}
              username={loginInput}
            />
            
            <div className="relative my-3">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-muted-foreground">
                  {i18n.__('vaiu.or')}
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Input
                id="loginInput"
                type="text"
                placeholder={i18n.__('login.input_placeholder')}
                required
                value={loginInput}
                onChange={(e) => setLoginInput(e.target.value)}
                className={error ? 'border-red-500' : ''}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={i18n.__('vaiu.password')}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={error ? 'border-red-500' : ''}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={i18n.__(showPassword ? 'login.hidePassword' : 'login.showPassword')}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <div className="text-right">
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto text-sm text-blue-600 hover:underline"
                  onClick={() => navigate('/forgot-password')}
                >
                  {i18n.__('vaiu.forgot_password') || 'Forgot password?'}
                </Button>
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading || !loginInput.trim() || !password.trim()}
            >
              {loading ? i18n.__('vaiu.signing_in') : i18n.__('vaiu.sign_in')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
